import React, { useMemo } from "react";
import { Box, Stack, Typography, Paper, Chip, Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PanelHeading from "./PanelHeading";
import { byStartTime, groupByWeekday, shortTime, todayWeekday } from "../../utils/schedule";

const HINT = "The recurring weekly class schedule from Course → Time Slots. "
  + "\"Capacity\" is the seat limit on the slot, not live availability.";

/**
 * The recurring weekly timetable, grouped by weekday with today first.
 *
 * Shows `seats` as **Capacity** and deliberately never renders `course_time_slots.remaining_seats`:
 * that column is only ever written as a copy of `seats` (CourseTimeSlot::storeTimeSlot /
 * updateTimeSlot) and there is no time-slot column on manage_student_records to derive it from, so
 * "seats left" here would always equal the full capacity.
 */
export default function WeeklyTimetablePanel({ slots }) {
  const today = todayWeekday();

  const days = useMemo(() => {
    const grouped = groupByWeekday(slots, (s) => s.week_days);
    grouped.forEach((d) => {
      d.rows = byStartTime(d.rows);
      d.isToday = d.key === today;
    });
    // Today leads; the rest keep their Monday-first order.
    return [...grouped].sort((a, b) => Number(b.isToday) - Number(a.isToday));
  }, [slots, today]);

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        border: "1px solid #eef0f4",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PanelHeading
        title="Weekly Timetable"
        hint={HINT}
        action={(
          <MuiLink
            component={RouterLink}
            to="/admin/timeslot"
            underline="hover"
            sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
          >
            Manage
          </MuiLink>
        )}
      />

      {days.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600 }}>
            No time slots yet
          </Typography>
          <MuiLink
            component={RouterLink}
            to="/admin/timeslot"
            underline="hover"
            sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
          >
            Add a time slot
          </MuiLink>
        </Stack>
      ) : (
        <Box sx={{ flex: 1, maxHeight: 340, overflowY: "auto", pr: 0.5 }}>
          <Stack spacing={1.5}>
            {days.map((d) => (
              <Box key={d.key}>
                <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                  <Typography
                    variant="caption"
                    sx={{ color: d.isToday ? "#7c3aed" : "#6b7280", fontWeight: 800, textTransform: "uppercase" }}
                  >
                    {d.name}
                  </Typography>
                  {d.isToday && (
                    <Chip size="small" label="Today" sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: "#ede9fe", color: "#7c3aed" }} />
                  )}
                </Stack>
                <Stack spacing={0.75}>
                  {d.rows.map((s) => (
                    <Stack
                      key={s.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: d.isToday ? "#faf5ff" : "#f9fafb",
                        borderLeft: `3px solid ${d.isToday ? "#7c3aed" : "#e5e7eb"}`,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }} noWrap>
                          {s.class_name || "Untitled slot"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap display="block">
                          {[s.course_name, s.location_name].filter(Boolean).join(" · ") || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="caption" sx={{ color: "#111827", fontWeight: 700 }} display="block">
                          {shortTime(s.start_time)}–{shortTime(s.end_time)}
                        </Typography>
                        {s.seats != null && (
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            Capacity {s.seats}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
